// FA204 / frank trap — every helper this export needs (convertToKebabCase,
// formatAttributes, MAX_DEPTH, compileChildren) is declared INSIDE
// compileToHtml's own body instead of at module scope. compileToHtml is the
// ONLY export, so there's no sibling to keep these split out for; a
// module-scope const/function is unresolvable once compileToHtml is
// individually frank.toJSON'd for platform delivery
// (project_frank_drops_module_local_helpers) — nested declarations inside the
// exported function's own source are never rewritten and keep working via
// normal JS closures.
export const compileToHtml = (props, level = 0) => {
  const MAX_DEPTH = 100

  const convertToKebabCase = (str) =>
    str
      .replace(/([a-z0-9])([A-Z])/gu, '$1-$2')
      .replace(/([A-Z])([A-Z][a-z])/gu, '$1-$2')
      .toLowerCase()

  const formatAttributes = (attrs, indent = 2, multiline = false) => {
    if (!attrs || typeof attrs !== 'object') {
      return ''
    }

    const entries = Object.entries(attrs).filter(
      ([key]) =>
        !['children', 'childProps', 'text', 'tag'].includes(key) &&
        typeof key === 'string' &&
        key.match(/^[a-zA-Z][a-zA-Z0-9]*$/u)
    )

    if (!multiline || entries.length <= 0) {
      return entries
        .map(([key, value]) => `${convertToKebabCase(key)}="${value}"`)
        .join(' ')
    }

    return entries
      .map(
        ([key, value]) =>
          `${' '.repeat(indent + 2)}${convertToKebabCase(key)}="${value}"`
      )
      .join('\n')
  }

  const compileChildren = (children, childProps = {}, level = 0) => {
    const indent = ' '.repeat(level * 2)
    let html = ''

    if (!children || typeof children !== 'object') {
      return html
    }

    Object.entries(children).forEach(([key, value]) => {
      if (!key.match(/^[a-zA-Z][a-zA-Z0-9]*$/u)) {
        return
      }

      const tagName = convertToKebabCase(key)
      const attributes = formatAttributes(value)

      if (!value || typeof value !== 'object') {
        return
      }

      if (!value.children) {
        html += `${indent}<${tagName}${attributes ? ` ${attributes}` : ''} />\n`
        return
      }

      // Element has children
      html += `${indent}<${tagName}${attributes ? ` ${attributes}` : ''}>\n`

      if (Array.isArray(value.children)) {
        value.children.forEach((child, index) => {
          if (!child || typeof child !== 'object') {
            return
          }

          const childAttributes = {
            ...(value.childProps || {}),
            ...(child.childProps || {})
          }

          // First-child and last-child handling
          if (
            value.childProps &&
            value.childProps[':first-child'] &&
            index === 0
          ) {
            Object.assign(childAttributes, value.childProps[':first-child'])
          }
          if (
            value.childProps &&
            value.childProps[':last-child'] &&
            index === value.children.length - 1
          ) {
            Object.assign(childAttributes, value.childProps[':last-child'])
          }

          const childAttrs = formatAttributes(childAttributes, level + 1, true)
          const childContent = child.text || ''
          html += `${indent}  <div${childAttrs ? ` ${childAttrs}` : ''}>${childContent}</div>\n`
        })
      }

      html += `${indent}</${tagName}>\n`
    })

    return html
  }

  if (level > MAX_DEPTH) {
    throw new Error('Maximum nesting depth exceeded')
  }

  if (!props || typeof props !== 'object') {
    throw new Error('Invalid input: props must be an object')
  }

  const indent = ' '.repeat(level * 2)

  // Handle root level element with tag
  if (props.tag) {
    const tagName = convertToKebabCase(props.tag)
    const attributes = formatAttributes(props)

    if (!props.children) {
      // Self-closing tag
      return `${indent}<${tagName}${attributes ? ` ${attributes}` : ''} />`
    }

    // Block-level tag with children
    const childrenHtml = compileChildren(
      props.children,
      props.childProps,
      level + 1
    )
    return `${indent}<${tagName}${attributes ? ` ${attributes}` : ''}>\n${childrenHtml}\n${indent}</${tagName}>`
  }

  return compileChildren(props, {}, level)
}
